import { AlertTriangle } from "lucide-react";
import { useAddItem } from "@/context/addItem";

import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";

import ManualEntryTab from "./ManualEntryTab";
import ExternalQRTab from "./ExternalQRTab";
import ReceiptOCRTab from "./ReceiptOCRTab";

export default function AddItemModal() {
  const {
    activeTab,
    setActiveTab,
    isDuplicate,
    isOffline,
    pendingItems,
    isProcessing,
    handleSubmit,
    handleForceSave
  } = useAddItem();

  return (
    <DialogContent className="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>Add New Item</DialogTitle>
        <DialogDescription>
          Add item details to track its expiration.
        </DialogDescription>
      </DialogHeader>
      
      {/* Offline Warning */}
      {isOffline && (
        <Alert className="mb-4 border-amber-500 bg-amber-50 text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You're currently offline. Items will be saved locally and synced when you're back online.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Duplicate Warning */}
      {isDuplicate && (
        <Alert className="mb-4 border-amber-500 bg-amber-50 text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex justify-between items-center">
            <span>This item appears to already exist in your inventory.</span>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={handleForceSave}
              className="border-amber-500 hover:bg-amber-100 text-amber-800"
            >
              Add Anyway
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Pending Items Count */}
      {pendingItems.length > 0 && (
        <div className="flex justify-between items-center mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <span className="text-sm font-medium text-green-800">
            {pendingItems.length} {pendingItems.length === 1 ? 'item' : 'items'} ready to be added.
          </span>
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 mb-4">
          <TabsTrigger value="manual">Manual</TabsTrigger>
          <TabsTrigger value="qr">QR Code</TabsTrigger>
          <TabsTrigger value="receipt">Receipt</TabsTrigger>
        </TabsList>
        
        <TabsContent value="manual" className="focus-visible:outline-none">
          <form onSubmit={handleSubmit}>
            <ManualEntryTab />
            <DialogFooter className="mt-6">
              <Button 
                type="submit" 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={isProcessing}
              >
                {isProcessing ? "Adding Item..." : pendingItems.length > 0 ? `Add ${pendingItems.length} Items` : "Add Item"}
              </Button>
            </DialogFooter>
          </form>
        </TabsContent>
        
        <TabsContent value="qr" className="focus-visible:outline-none">
          <ExternalQRTab />
        </TabsContent>
        
        <TabsContent value="receipt" className="focus-visible:outline-none">
          <ReceiptOCRTab />
        </TabsContent>
      </Tabs>
    </DialogContent>
  );
}
